/**
 * Task 11b — Live-formatting US phone input.
 *
 * Storage: parent receives ``onChange(rawDigits)`` where ``rawDigits`` is a
 * 0–10-char string of digits only. Display: ``(XXX) XXX-XXXX`` formatted
 * as the user types.
 *
 * Props:
 *   * ``value``         — string (any format). Component extracts digits.
 *   * ``onChange(raw)`` — emits raw 10-digit string (or partial digits).
 *   * ``onValidityChange?(isValid, error)`` — optional bubble.
 *   * ``placeholder``   — defaults to ``(555) 555-5555``.
 *   * ``autoComplete``  — defaults to ``tel-national``.
 *   * ``disabled``, ``required``, ``className``, ``data-testid``.
 *
 * Implementation notes:
 *   * Backspace deletes the last DIGIT, not the trailing space / paren.
 *   * Paste accepts ``+1 (415) 555-1234``, ``4155551234``, ``1-415-555-1234``,
 *     ``415.555.1234``, etc. — ``extractDigits`` handles the country-code
 *     strip + non-digit purge in one pass.
 *   * Caps at 10 digits.
 *   * ``inputMode="tel"`` so mobile keyboards show the dial pad.
 */
import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { extractDigits, formatPhoneWhileTyping, isValidUSPhone } from "@/lib/phone";

export default function PhoneInput({
    value,
    onChange,
    onValidityChange,
    placeholder = "(555) 555-5555",
    autoComplete = "tel-national",
    disabled = false,
    required = false,
    className = "",
    autoFocus = false,
    "data-testid": testId = "phone-input",
}) {
    const digits = extractDigits(value).slice(0, 10);
    const display = formatPhoneWhileTyping(digits);
    const [touched, setTouched] = useState(false);

    // Error rules:
    //   * required + empty → "Phone is required"
    //   * non-empty + not 10 digits → "Phone must be 10 digits"
    //   * 10 digits + NANP-invalid → "Invalid US phone number"
    //   * empty + !required → no error
    let error = null;
    if (digits.length === 0) {
        if (required) error = "Phone is required";
    } else if (digits.length < 10) {
        error = "Phone must be 10 digits";
    } else if (!isValidUSPhone(digits)) {
        error = "Invalid US phone number";
    }

    useEffect(() => {
        if (onValidityChange) onValidityChange(!error, error);
    }, [error]);

    function handleChange(e) {
        const next = extractDigits(e.target.value).slice(0, 10);
        onChange(next);
    }

    return (
        <div className="space-y-1">
            <Input
                data-testid={testId}
                type="tel"
                inputMode="tel"
                autoComplete={autoComplete}
                pattern="[0-9()\-\s]*"
                disabled={disabled}
                placeholder={placeholder}
                value={display}
                onChange={handleChange}
                onBlur={() => setTouched(true)}
                autoFocus={autoFocus}
                className={className}
                aria-invalid={touched && !!error || undefined}
            />
            {touched && error && (
                <p className="text-xs text-red-600" data-testid={`${testId}-error`}>
                    {error}
                </p>
            )}
        </div>
    );
}
